import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, Loader2, Plus } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { ASSESSMENT_STATUS_LABELS } from "@/config/assessmentCatalog";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-600 border-transparent",
  in_progress: "bg-sky-100 text-sky-800 border-transparent",
  awaiting_information: "bg-amber-100 text-amber-800 border-transparent",
  completed_by_assessor: "bg-indigo-100 text-indigo-800 border-transparent",
  awaiting_manager_review: "bg-indigo-100 text-indigo-800 border-transparent",
  approved: "bg-ukgreen-light text-ukgreen border-transparent",
  returned_for_amendment: "bg-orange-100 text-orange-800 border-transparent",
  cancelled: "bg-slate-100 text-slate-500 border-transparent",
};

export default function AssessmentsTab({ child }) {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canCreate = hasPermission("assessments:create");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduledDate, setScheduledDate] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/assessments", { params: { child_id: child.id } });
      setItems(data.items);
    } catch (err) {
      if (err?.response?.status !== 404) toast.error(formatApiError(err));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [child.id]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/assessments/children/${child.id}`, { scheduled_date: scheduledDate || null });
      toast.success(`Assessment ${data.assessment_number} created — you are recorded as the assessor`);
      navigate(`/assessments/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="assessments-tab">
      {canCreate && (
        <Card>
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Schedule initial assessment (date)</Label>
              <Input type="date" className="h-10 sm:w-52" data-testid="assessment-schedule-date"
                value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} />
            </div>
            <Button onClick={create} disabled={creating} data-testid="assessment-create-button"
              className="h-10 bg-primary hover:bg-brand-hover text-white font-semibold">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-2" /> Start initial assessment</>}
            </Button>
            <p className="text-xs text-slate-500">Creating an assessment moves the case to “Initial Assessment Scheduled”.</p>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center" data-testid="assessments-empty">
          <ClipboardCheck className="mx-auto h-8 w-8 text-slate-300 mb-3" />
          <p className="text-sm text-slate-600">No assessments yet for this case.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="assessments-list">
          {items.map((a) => (
            <Card key={a.id} data-testid={`assessment-row-${a.id}`} className="hover:border-primary/40 transition-colors duration-200">
              <CardContent className="p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => navigate(`/assessments/${a.id}`)} data-testid={`assessment-open-${a.id}`}
                      className="font-medium text-primary hover:underline text-left">
                      {a.assessment_number} — v{a.version}
                    </button>
                    <Badge className={STATUS_STYLES[a.status]} data-testid={`assessment-status-${a.id}`}>
                      {ASSESSMENT_STATUS_LABELS[a.status]}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Assessor: {a.assessor_name} · {a.assessment_date ? `Assessed ${a.assessment_date}` : a.scheduled_date ? `Scheduled ${a.scheduled_date}` : "Not scheduled"}
                    {a.submitted_at ? ` · Submitted ${new Date(a.submitted_at).toLocaleDateString("en-GB")}` : ""}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
